import { useAuthContext } from "@context/AuthProvider"
import { useUserStore } from "@hooks/useUserStore"
import {
	getMetadataFromPath,
	initializeTealium,
	patchTealiumUserData,
	updateTealiumPageData
} from "@services/TealiumService"
import type { RouteMetadata } from "@services/TealiumService"
import { useOAuthStore } from "@stores/oauthStore"
import { useEffect, useRef } from "react"
import { useLocation } from "react-router-dom"

function getRouteMetadata(pathname: string): RouteMetadata {
	// Remove leading UI_BASE_URL if present
	const cleanPath = pathname.replace(/^\/ui/, "")
	return getMetadataFromPath(cleanPath)
}

function getPageTitle(pathname: string): string {
	const baseTitle = "CruisingPower Professional Portal"

	const titleMap: Record<string, string> = {
		"/home": "Home page",
		"/profile": "Profile",
		"/profile/communications": "Communications - Profile",
		"/profile/agency": "Agency Information - Profile",
		"/profile/security": "Security Settings - Profile",
		"/account-compliance": "Security Settings - Profile",
		"/user-management": "User Management",
		"/insights": "Insights",
		"/promotion": "Promotions",
		"/travel-docs": "Travel Documents",
		"/itinerary": "Itineraries",
		"/alerts": "Alerts",
		"/register": "Agent Registration",
		"/login": "Login"
	}

	const matchedRoute = Object.keys(titleMap).find((route) =>
		pathname.startsWith(route)
	)

	if (matchedRoute) {
		return `${titleMap[matchedRoute]} | ${baseTitle}`
	}
	return baseTitle
}

function RouteAnalytics() {
	const location = useLocation()
	const { userProfile } = useUserStore()
	const { isLoading: isAuthLoading } = useAuthContext()
	const isOAuthInProgress = useOAuthStore((state) => state.isOAuthInProgress)
	const userProfileRef = useRef(userProfile)
	const isScriptLoadedRef = useRef(false)

	useEffect(() => {
		userProfileRef.current = userProfile
	}, [userProfile])

	// Waits for auth to resolve so the initial view already carries agent/agency
	// data - avoiding a premature view followed by a separate patch on refresh.
	useEffect(() => {
		if (isAuthLoading) {
			return
		}

		const timeoutId = setTimeout(() => {
			const metadata = isOAuthInProgress
				? { section: "oauth", page: "oauth" }
				: getRouteMetadata(location.pathname)

			if (!isScriptLoadedRef.current) {
				// First time initialization
				initializeTealium(metadata, userProfileRef.current ?? undefined)
				isScriptLoadedRef.current = true
			} else {
				// Subsequent page views
				updateTealiumPageData(metadata, userProfileRef.current ?? undefined)
			}
			const newTitle = getPageTitle(location.pathname)
			document.title = newTitle
		}, 100)

		return () => clearTimeout(timeoutId)
	}, [location.pathname, isOAuthInProgress, isAuthLoading])

	// Keeps agent/agency data in sync if it changes after the initial view
	// (e.g. switching agencies mid-session), without re-firing the page view.
	// Depends only on the identity fields utag_data cares about, so unrelated
	// profile updates (e.g. cruise data merged in later) don't trigger extra patches.
	useEffect(() => {
		if (isScriptLoadedRef.current) {
			patchTealiumUserData(userProfile ?? undefined)
		}
	}, [userProfile?.agentId, userProfile?.agencyId, userProfile?.countryCode])

	return null
}

export { RouteAnalytics }
